// src/components/transfers/ProductPicker.jsx
import React, { useState } from 'react';
import { Package, Search } from 'lucide-react';

const ProductPicker = ({ products, selectedItems, onAdd }) => {
  const [search, setSearch] = useState('');
  const [pickedId, setPickedId] = useState(null);
  const [count, setCount] = useState(1);

  const filtered = products.filter(p => {
    if (!search) return true;
    const kw = search.toLowerCase();
    return p.name?.toLowerCase().includes(kw) || p.barcode?.toLowerCase().includes(kw);
  });

  const picked = products.find(p => p.id === pickedId);
  const hasBox = !!(picked?.box_quantity > 0);
  const maxVal = hasBox ? Math.floor(picked.stock / picked.box_quantity) : (picked?.stock || 0);
  const alreadyAdded = selectedItems.some(i => i.product_id === pickedId);

  const handleAdd = () => {
    if (!pickedId || !picked) return;
    if (count < 1 || count > maxVal) return;
    onAdd({
      product_id: picked.id,
      product_name: picked.name,
      product_image: picked.image,
      box_quantity: picked.box_quantity,
      stock: picked.stock,
      transfer_unit: hasBox ? 'box' : 'unit',
      box_count: hasBox ? parseInt(count) : null,
      quantity: hasBox ? parseInt(count) * picked.box_quantity : parseInt(count),
    });
    setPickedId(null);
    setCount(1);
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="搜索商品名称或条码..."
          className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400" />
      </div>

      <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
        {filtered.map(p => {
          const isSelected = pickedId === p.id;
          const hb = p.box_quantity > 0;
          const maxBox = hb ? Math.floor(p.stock / p.box_quantity) : 0;
          const noStock = hb ? maxBox === 0 : p.stock === 0;
          return (
            <div key={p.id} onClick={() => !noStock && setPickedId(isSelected ? null : p.id)}
              className={`relative border-2 rounded-xl p-2 cursor-pointer transition-all ${
                noStock ? 'opacity-40 cursor-not-allowed border-gray-200' :
                isSelected ? 'border-blue-500 bg-blue-50 shadow-md' :
                'border-gray-200 hover:border-blue-300 hover:shadow-sm'}`}>
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-1.5">
                {p.image
                  ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  : <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-gray-300" /></div>}
              </div>
              <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight">{p.name}</p>
              <div className="mt-1 text-xs">
                {hb ? <span className={maxBox === 0 ? 'text-red-500' : 'text-orange-600'}>📦 可调 {maxBox} 箱</span>
                  : <span className={p.stock === 0 ? 'text-red-500' : 'text-gray-500'}>库存 {p.stock}</span>}
              </div>
              {hb && <div className="absolute top-1 right-1 bg-orange-500 text-white text-xs px-1 py-0.5 rounded">{p.box_quantity}/箱</div>}
              {isSelected && <div className="absolute top-1 left-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"><span className="text-white text-xs">✓</span></div>}
            </div>
          );
        })}
      </div>

      {picked && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
            {picked.image
              ? <img src={picked.image} alt={picked.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-gray-300" /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{picked.name}</p>
            {hasBox ? <p className="text-xs text-gray-500">1箱={picked.box_quantity}个 · 可调{maxVal}箱</p>
              : <p className="text-xs text-gray-500">库存{picked.stock}个</p>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setCount(c => Math.max(1, c - 1))} className="w-7 h-7 rounded-lg bg-white border flex items-center justify-center font-bold hover:bg-gray-100">−</button>
            <input type="number" min="1" max={maxVal} value={count}
              onChange={e => setCount(Math.min(maxVal, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-14 text-center border rounded-lg py-1 text-sm font-bold" />
            <span className="text-xs text-gray-500">{hasBox ? '箱' : '个'}</span>
            <button onClick={() => setCount(c => Math.min(maxVal, c + 1))} className="w-7 h-7 rounded-lg bg-white border flex items-center justify-center font-bold hover:bg-gray-100">＋</button>
          </div>
          <button onClick={handleAdd} disabled={alreadyAdded || count < 1 || count > maxVal}
            className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {alreadyAdded ? '已添加' : '加入'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductPicker;
